/**
 * Application entrypoint.
 *
 * This file wires up global configuration and renders the React app into the DOM.
 *
 * High-level flow:
 * 1) Initialize i18n (`i18next`) with our bundled translation resources.
 * 2) Create the React root and mount provider layers:
 *    - `StyleSheetManager` for styled-components DOM prop filtering
 *    - `I18nextProvider` for translations
 *    - `Provider` + `PersistGate` for Redux + persisted state
 *    - `BrowserRouter` for routing
 * 3) Optionally report performance metrics via `reportWebVitals()`.
 */
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import "./styles/visual-qa-hotfix.css";
import reportWebVitals from "./reportWebVitals";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "flag-icons/css/flag-icons.min.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { store, persistor } from "./store/store";
import { PersistGate } from 'redux-persist/integration/react';
import i18next from "i18next";
import { I18nextProvider } from "react-i18next";
import { StyleSheetManager } from "styled-components";
import moment from "moment-timezone";
import { normalizeSupportedLanguage } from "./i18n/supportedUiLanguages";
import "moment/locale/pl";

// Silence all console output in production so end-users see a clean DevTools.
if (process.env.NODE_ENV === 'production') {
  const noop = () => {};
  ['log', 'warn', 'error', 'info', 'debug', 'trace', 'dir', 'table', 'group', 'groupEnd', 'groupCollapsed'].forEach((method) => {
    window.console[method] = noop;
  });
}

// Get language from localStorage or default to 'pl'.
// We normalize regional variants like `pl-PL` -> `pl` because our resources are language-only.
const FALLBACK_LANGUAGE = 'pl';
const rawLanguage = localStorage.getItem('speakable-language') || FALLBACK_LANGUAGE;
const savedLanguage = normalizeSupportedLanguage(rawLanguage, FALLBACK_LANGUAGE);

const languageResourcePromises = new Map();

/**
 * Loads one language bundle on demand instead of bundling all locales into startup JS.
 *
 * @param {string} languageCode
 * @returns {Promise<object|null>}
 */
const loadGlobalTranslations = async (languageCode) => {
  const normalized = normalizeSupportedLanguage(languageCode, FALLBACK_LANGUAGE);
  try {
    const translationModule = await import(
      /* webpackChunkName: "i18n-global-[request]" */
      `./components/transitions/${normalized}/global.json`
    );
    return translationModule?.default || translationModule;
  } catch (error) {
    console.warn(`[i18n] Failed to load global translations for "${normalized}"`, error);
    return null;
  }
};

/**
 * Returns a cached loading promise per language to avoid duplicate network/chunk work.
 *
 * @param {string} languageCode
 * @returns {Promise<object|null>}
 */
const getLanguageLoadPromise = (languageCode) => {
  const normalized = normalizeSupportedLanguage(languageCode, FALLBACK_LANGUAGE);
  if (!languageResourcePromises.has(normalized)) {
    languageResourcePromises.set(normalized, loadGlobalTranslations(normalized));
  }
  return languageResourcePromises.get(normalized);
};

/**
 * Makes sure requested language is available in i18next before switching to it.
 *
 * @param {string} languageCode
 * @returns {Promise<string>}
 */
const ensureLanguageResources = async (languageCode) => {
  const normalized = normalizeSupportedLanguage(languageCode, FALLBACK_LANGUAGE);
  if (i18next.hasResourceBundle(normalized, "global")) {
    return normalized;
  }

  const globalTranslations = await getLanguageLoadPromise(normalized);
  if (globalTranslations && typeof globalTranslations === "object") {
    i18next.addResourceBundle(normalized, "global", globalTranslations, true, true);
  }

  return normalized;
};

// Keep moment's locale in sync with the current UI language so month/day names render correctly
// (e.g. "18 lutego 2026" instead of "February 18, 2026").
/**
 * Executes logic in `normalizeLang`.
 * @param {any} lng Input value.
 * @returns {any} Returns a value of type `any`.
 */
const normalizeLang = (lng) => normalizeSupportedLanguage(lng, FALLBACK_LANGUAGE);
/**
 * Sets in `setMomentLocale`.
 * @param {any} lng Input value.
 * @returns {void} No return value.
 */
const setMomentLocale = (lng) => {
  const lang = normalizeLang(lng);
  moment.locale(lang === "pl" ? "pl" : "en");
};
const root = ReactDOM.createRoot(document.getElementById("root"));

// Prevent styled-components from forwarding custom styling props to DOM nodes.
// Keep filtering DOM-only to avoid breaking custom React components that rely on these props.
const BLOCKED_DOM_PROPS = new Set([
  'variant',
  'padding',
  'interactive',
  'noPadding',
  'align',
  'justify',
  'gap',
  'size'
]);

/**
 * styled-components prop forwarding filter.
 *
 * When styled-components wraps a native DOM element (e.g. `div`), any unknown props can end up on
 * the actual DOM node and trigger React warnings. We block a curated set of styling-only props
 * while allowing all props for custom React components.
 *
 * @param {string} prop
 * @param {string|React.ComponentType} elementToBeCreated
 * @returns {boolean} true when the prop should be forwarded to the underlying element
 */
const shouldForwardProp = (prop, elementToBeCreated) => {
  if (typeof elementToBeCreated === 'string' && BLOCKED_DOM_PROPS.has(prop)) {
    return false;
  }
  return true;
};

const isLocalBrowserSession =
  typeof window !== 'undefined' &&
  /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname || '');

const LOCALHOST_SW_CLEANUP_KEY = 'speakable-localhost-sw-cleanup-v1';
const SPEAKABLE_CACHE_CLEANUP_PREFIX = 'speakable-cache-cleanup-v2:';
const SPEAKABLE_CSS_RECOVERY_PREFIX = 'speakable-css-recovery-v2:';
const SPEAKABLE_CSS_RECOVERY_MAX_ATTEMPTS = 3;
const LEGACY_SW_PATHS = new Set(['/sw.js', '/service-worker.js']);

const isSpeakableBrowserSession =
  typeof window !== 'undefined' &&
  /(^|\.)speakable\.online$/i.test(window.location.hostname || '');

const clearLocalhostServiceWorkersAndCaches = async () => {
  if (typeof window === 'undefined') return false;

  let cleanedUp = false;

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length > 0) {
        await Promise.all(registrations.map((registration) => registration.unregister()));
        cleanedUp = true;
      }
    }
  } catch (_) {
    // Ignore cleanup errors on localhost and continue bootstrapping.
  }

  try {
    if ('caches' in window) {
      const cacheKeys = await caches.keys();
      if (cacheKeys.length > 0) {
        await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
        cleanedUp = true;
      }
    }
  } catch (_) {
    // Ignore cache cleanup failures on localhost.
  }

  return cleanedUp;
};

const getCurrentFrontendAssetFingerprint = () => {
  if (typeof document === 'undefined') return 'unknown';

  try {
    const assetUrls = Array.from(
      document.querySelectorAll('script[src], link[rel="stylesheet"][href]')
    )
      .map((element) => element.getAttribute('src') || element.getAttribute('href') || '')
      .filter((assetUrl) => /\/static\/(js|css)\//.test(assetUrl))
      .map((assetUrl) => {
        try {
          const parsedUrl = new URL(assetUrl, window.location.origin);
          return parsedUrl.origin === window.location.origin ? parsedUrl.pathname : '';
        } catch (_) {
          return assetUrl;
        }
      })
      .filter(Boolean)
      .sort();

    return assetUrls.join('|') || 'unknown';
  } catch (_) {
    return 'unknown';
  }
};

const getBrowserStorage = () => {
  if (typeof window === 'undefined') return null;

  try {
    const testKey = '__speakable_storage_test__';
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    return window.localStorage;
  } catch (_) {
    try {
      const testKey = '__speakable_session_storage_test__';
      window.sessionStorage.setItem(testKey, '1');
      window.sessionStorage.removeItem(testKey);
      return window.sessionStorage;
    } catch (__) {
      return null;
    }
  }
};

const clearSpeakableServiceWorkersAndCaches = async () => {
  if (typeof window === 'undefined') return false;

  let cleanedUp = false;

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      const legacyRegistrations = registrations.filter((registration) => {
        const scriptUrl =
          registration.active?.scriptURL ||
          registration.waiting?.scriptURL ||
          registration.installing?.scriptURL ||
          '';

        try {
          const parsedUrl = new URL(scriptUrl, window.location.origin);
          return parsedUrl.origin === window.location.origin && LEGACY_SW_PATHS.has(parsedUrl.pathname);
        } catch (_) {
          return false;
        }
      });

      if (legacyRegistrations.length > 0) {
        await Promise.all(
          legacyRegistrations.map(async (registration) => {
            try {
              await registration.update();
            } catch (_) {
              // Updating stale service workers is best-effort.
            }
            return registration.unregister();
          })
        );
        cleanedUp = true;
      }
    }
  } catch (_) {
    // Ignore service worker cleanup failures and continue bootstrapping.
  }

  try {
    if ('caches' in window) {
      const cacheKeys = await caches.keys();

      if (cacheKeys.length > 0) {
        await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
        cleanedUp = true;
      }
    }
  } catch (_) {
    // Ignore cache cleanup failures and continue bootstrapping.
  }

  return cleanedUp;
};

const hasLoadedMainStylesheet = () => {
  if (typeof document === 'undefined') return true;

  const mainCssLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"][href]')).filter(
    (link) => /\/static\/css\/main\.[^/]+\.css($|\?)/.test(link.href || '')
  );

  if (mainCssLinks.length === 0) return true;

  return mainCssLinks.some((link) => {
    try {
      if (!link.sheet) return false;
      return link.sheet.cssRules.length > 0;
    } catch (_) {
      return true;
    }
  });
};

const getVisibleElement = (selector) => {
  if (typeof document === 'undefined') return null;

  return Array.from(document.querySelectorAll(selector)).find((element) => {
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      rect.width > 0 &&
      rect.height > 0
    );
  });
};

const looksLikeUnstyledFunnelShell = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;

  if (!hasLoadedMainStylesheet()) return true;

  const visiblePrimaryAction = getVisibleElement('[data-speakable-primary-action]');
  if (visiblePrimaryAction) {
    const style = window.getComputedStyle(visiblePrimaryAction);
    const rect = visiblePrimaryAction.getBoundingClientRect();
    const displayLooksStyled = ['flex', 'inline-flex'].includes(style.display);
    const radiusLooksStyled = Number.parseFloat(style.borderTopLeftRadius || '0') >= 8;
    const sizeLooksStyled = rect.height >= 40 && rect.width >= 96;

    if (!displayLooksStyled || !radiusLooksStyled || !sizeLooksStyled) {
      return true;
    }
  }

  const visibleLogo = getVisibleElement('[data-speakable-header-logo-img]');
  if (visibleLogo) {
    const rect = visibleLogo.getBoundingClientRect();
    if (rect.width > 72 || rect.height > 72) {
      return true;
    }
  }

  return false;
};

const scheduleFunnelShellRecoveryCheck = () => {
  if (!isSpeakableBrowserSession || typeof window === 'undefined') return;

  window.setTimeout(async () => {
    const storage = getBrowserStorage();
    const recoveryKey = `${SPEAKABLE_CSS_RECOVERY_PREFIX}${getCurrentFrontendAssetFingerprint()}:${
      window.location.pathname || '/'
    }`;

    if (!looksLikeUnstyledFunnelShell()) {
      try {
        storage?.removeItem(recoveryKey);
      } catch (_) {
        // Ignore storage cleanup failures.
      }
      return;
    }

    try {
      const currentAttempts = Number.parseInt(storage?.getItem(recoveryKey) || '0', 10) || 0;
      if (currentAttempts >= SPEAKABLE_CSS_RECOVERY_MAX_ATTEMPTS) return;
      storage?.setItem(recoveryKey, String(currentAttempts + 1));
    } catch (_) {
      // Continue with recovery if storage is unavailable.
    }

    await clearSpeakableServiceWorkersAndCaches();
    window.location.reload();
  }, 2500);
};

/**
 * Renders the full application tree.
 *
 * @returns {void}
 */
const renderApp = () => {
  root.render(
    <React.StrictMode>
      {/* Provider order matters: outer providers wrap inner ones so all descendants can consume them. */}
      <StyleSheetManager shouldForwardProp={shouldForwardProp}>
        <I18nextProvider i18n={i18next}>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <App />
              </BrowserRouter>
            </PersistGate>
          </Provider>
        </I18nextProvider>
      </StyleSheetManager>
    </React.StrictMode>
  );
};

/**
 * Bootstraps i18n with a minimal set of resources and renders app afterwards.
 * This reduces startup JS parse cost for users who never switch language.
 *
 * @returns {Promise<void>}
 */
const bootstrapI18nAndRender = async () => {
  if (isLocalBrowserSession) {
    try {
      const cleanupState = sessionStorage.getItem(LOCALHOST_SW_CLEANUP_KEY);
      if (cleanupState !== 'done') {
        sessionStorage.setItem(LOCALHOST_SW_CLEANUP_KEY, 'running');
        const cleanedUp = await clearLocalhostServiceWorkersAndCaches();
        sessionStorage.setItem(LOCALHOST_SW_CLEANUP_KEY, 'done');
        if (cleanedUp) {
          window.location.reload();
          return;
        }
      }
    } catch (_) {
      // Ignore sessionStorage issues and continue with normal startup.
    }
  }

  if (isSpeakableBrowserSession) {
    try {
      const storage = getBrowserStorage();
      const cleanupKey = `${SPEAKABLE_CACHE_CLEANUP_PREFIX}${getCurrentFrontendAssetFingerprint()}`;
      const cleanupState = storage?.getItem(cleanupKey);
      if (cleanupState !== 'done') {
        storage?.setItem(cleanupKey, 'running');
        const cleanedUp = await clearSpeakableServiceWorkersAndCaches();
        storage?.setItem(cleanupKey, 'done');
        if (cleanedUp) {
          window.location.reload();
          return;
        }
      }
    } catch (_) {
      // Ignore sessionStorage issues and continue with normal startup.
    }
  }

  const preloadLanguages = [FALLBACK_LANGUAGE];
  if (savedLanguage !== FALLBACK_LANGUAGE) {
    preloadLanguages.push(savedLanguage);
  }

  const loadedEntries = await Promise.all(
    preloadLanguages.map(async (lng) => [lng, await getLanguageLoadPromise(lng)])
  );

  const resources = loadedEntries.reduce((acc, [lng, globalTranslations]) => {
    if (globalTranslations && typeof globalTranslations === "object") {
      acc[lng] = { global: globalTranslations };
    }
    return acc;
  }, {});

  const initialLanguage = resources[savedLanguage] ? savedLanguage : FALLBACK_LANGUAGE;

  await i18next.init({
    // Escape interpolation values to avoid accidental HTML injection in translations.
    interpolation: { escapeValue: true },
    lng: initialLanguage,
    fallbackLng: FALLBACK_LANGUAGE,
    load: "languageOnly",
    resources,
    ns: ["global"],
    defaultNS: "global",
    react: { useSuspense: false },
  });

  setMomentLocale(i18next.language || savedLanguage);
  i18next.on("languageChanged", setMomentLocale);

  const originalChangeLanguage = i18next.changeLanguage.bind(i18next);
  i18next.changeLanguage = async (lng, ...args) => {
    const normalized = await ensureLanguageResources(lng);
    return originalChangeLanguage(normalized, ...args);
  };

  renderApp();
  scheduleFunnelShellRecoveryCheck();
};

bootstrapI18nAndRender().catch((error) => {
  console.error("[i18n] Bootstrap failed, continuing with fallback language.", error);
  i18next
    .init({
      interpolation: { escapeValue: true },
      lng: FALLBACK_LANGUAGE,
      fallbackLng: FALLBACK_LANGUAGE,
      resources: {},
      ns: ["global"],
      defaultNS: "global",
      react: { useSuspense: false },
    })
    .finally(renderApp);
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
